/**
 * View areas: polygons with free vertices stored as [east_m, north_m]
 * offsets from their item's point, so moving the item moves the area.
 */
import { bearingOf, localProjection, nearestOnTrack, positionAt, wrap360, } from "./measure.js";
const DEG = Math.PI / 180;
/** Default near edge: about 12 % of the reach, at least 10 m. */
export function defaultNearM(reachM) {
    return Math.max(10, Math.round((reachM * 0.12) / 5) * 5);
}
function polar(bearingDeg, distanceM) {
    const a = bearingDeg * DEG;
    return [
        Math.round(distanceM * Math.sin(a) * 10) / 10,
        Math.round(distanceM * Math.cos(a) * 10) / 10,
    ];
}
/** The default 4-corner cone: far edge, then near edge. */
export function buildCone(bearingDeg, fovDeg, reachM, nearM = defaultNearM(reachM)) {
    const half = fovDeg / 2;
    return [
        polar(bearingDeg - half, reachM),
        polar(bearingDeg + half, reachM),
        polar(bearingDeg + half, nearM),
        polar(bearingDeg - half, nearM),
    ];
}
/** "Area around camera": a ring polygon centred on the item. */
export function buildRing(radiusM, corners = 12) {
    return Array.from({ length: corners }, (_, i) => polar((360 / corners) * i, radiusM));
}
export function pointInPolygon(p, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        if (yi > p[1] !== yj > p[1]) {
            const x = ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi;
            if (p[0] < x)
                inside = !inside;
        }
    }
    return inside;
}
export function polygonAreaM2(vertices) {
    let a = 0;
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
        a += vertices[j][0] * vertices[i][1] - vertices[i][0] * vertices[j][1];
    }
    return Math.abs(a) / 2;
}
export function summarizeViewArea(vertices) {
    let cx = 0;
    let cy = 0;
    let reach = 0;
    let near = Infinity;
    for (const [x, y] of vertices) {
        cx += x / vertices.length;
        cy += y / vertices.length;
        const d = Math.hypot(x, y);
        reach = Math.max(reach, d);
        near = Math.min(near, d);
    }
    const bearing = Math.round(bearingOf(cx, cy));
    const containsItem = pointInPolygon([0, 0], vertices);
    let fov = 360;
    if (!containsItem) {
        let spread = 0;
        for (const [x, y] of vertices) {
            let d = wrap360(bearingOf(x, y) - bearing);
            if (d > 180)
                d -= 360;
            spread = Math.max(spread, Math.abs(d));
        }
        fov = Math.round(Math.min(360, spread * 2));
    }
    return {
        bearingDeg: bearing,
        fovDeg: fov,
        reachM: Math.round(reach),
        nearM: Math.round(near === Infinity ? 0 : near),
        containsItem,
    };
}
/** Where the line from the item meets its area, or null when inside it. */
export function viewAreaLinkPoint(vertices) {
    if (vertices.length < 3 || pointInPolygon([0, 0], vertices))
        return null;
    let best = vertices[0];
    let bestD = Infinity;
    for (let i = 0; i < vertices.length; i++) {
        const a = vertices[i];
        const b = vertices[(i + 1) % vertices.length];
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const l2 = dx * dx + dy * dy || 1;
        const t = Math.max(0, Math.min(1, -(a[0] * dx + a[1] * dy) / l2));
        const x = a[0] + dx * t;
        const y = a[1] + dy * t;
        const d = x * x + y * y;
        if (d < bestD) {
            bestD = d;
            best = [x, y];
        }
    }
    return best;
}
export function vertexToLngLat(origin, vertex) {
    return localProjection(origin).toLngLat(vertex);
}
export function lngLatToVertex(origin, lngLat) {
    const [x, y] = localProjection(origin).toXY(lngLat);
    return [Math.round(x * 10) / 10, Math.round(y * 10) / 10];
}
export function viewAreaToLngLats(origin, vertices) {
    const projection = localProjection(origin);
    return vertices.map((v) => projection.toLngLat(v));
}
/** Insert a vertex halfway between vertex i and the next one. */
export function insertMidpoint(vertices, index) {
    const a = vertices[index];
    const b = vertices[(index + 1) % vertices.length];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    return [...vertices.slice(0, index + 1), mid, ...vertices.slice(index + 1)];
}
/** Remove a vertex; a polygon keeps at least 3. */
export function removeVertex(vertices, index) {
    if (vertices.length <= 3)
        return vertices;
    return vertices.filter((_, i) => i !== index);
}
/** Rotate an area around its item so it faces a new bearing. */
export function rotateViewArea(vertices, toBearingDeg) {
    const { bearingDeg } = summarizeViewArea(vertices);
    const delta = (toBearingDeg - bearingDeg) * DEG;
    const cos = Math.cos(delta);
    const sin = Math.sin(delta);
    // Compass rotation (clockwise) in an east/north frame
    return vertices.map(([x, y]) => [
        Math.round((x * cos + y * sin) * 10) / 10,
        Math.round((-x * sin + y * cos) * 10) / 10,
    ]);
}
/** Within this distance of a track, "aim at route" looks along the track. */
const ON_ROUTE_M = 15;
/**
 * Bearing that aims an item at the nearest track. On the route it looks
 * along the direction of travel.
 */
export function aimAtRoute(point, tracks) {
    let best = null;
    for (const track of tracks) {
        const nearest = nearestOnTrack(track, point);
        if (nearest && (!best || nearest.distanceM < best.d)) {
            best = { track, m: nearest.m, d: nearest.distanceM };
        }
    }
    if (!best)
        return null;
    if (best.d < ON_ROUTE_M) {
        return positionAt(best.track, best.m).bearing;
    }
    const target = positionAt(best.track, best.m).lngLat;
    const [x, y] = localProjection(point).toXY(target);
    return bearingOf(x, y);
}
